import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Footer from "../../../customComponents/Footer";
import useOtcDesk from "./useOtcDesk";
import OtcRfqForm from "./OtcRfqForm";
import OtcRfqTradingPage from "./OtcRfqTradingPage";
import "./otc-desk.css";

const WHY_TRADE_LIST = [
  {
    icon: "/images/otc-desk-img/otc_desk_list_icon.svg",
    title: "Expert Brokers",
    desc: "Professional OTC brokers handle every trade with precision and care.",
  },
  {
    icon: "/images/otc-desk-img/otc_desk_list_icon2.svg",
    title: "Zero Slippage",
    desc: "Best price guarantees on all OTC orders with no slippage impact.",
  },
  {
    icon: "/images/otc-desk-img/otc_desk_list_icon3.svg",
    title: "Qualified Desk",
    desc: "Licensed and certified OTC trading desk with full regulatory compliance.",
  },
  {
    icon: "/images/otc-desk-img/otc_desk_list_icon4.svg",
    title: "No Fees",
    desc: "Zero trading fees for qualified OTC orders above minimum threshold.",
  },
  {
    icon: "/images/otc-desk-img/otc_desk_list_icon5.svg",
    title: "Global Reach",
    desc: "Trade across all global markets 24/7 with our worldwide broker network.",
  },
  {
    icon: "/images/otc-desk-img/otc_desk_list_icon6.svg",
    title: "Confidential",
    desc: "Full privacy and NDA-protected trading for institutional clients.",
  },
];

const BUILT_FEATURES = [
  {
    icon: "/images/otc-desk-img/otc_info_icon.png",
    title: "Competitive institutional pricing",
    desc: "Access competitive institutional rates tailored to your trade size, volume, and execution needs.",
  },
  {
    icon: "/images/otc-desk-img/otc_info_icon3.png",
    title: "Deep liquidity for large orders",
    desc: "Execute substantial crypto transactions with access to deep, curated liquidity.",
  },
  {
    icon: "/images/otc-desk-img/otc_info_icon2.png",
    title: "Reduced market impact",
    desc: "Trade large positions without relying solely on public order books, helping maintain market execution.",
  },
  {
    icon: "/images/otc-desk-img/otc_info_icon4.png",
    title: "Dedicated trade support",
    desc: "Get direct support from our OTC team throughout execution and settlement.",
  },
];


function RfqFields({ desk, onSubmit, passThrough = false }) {
  return (
    <OtcRfqForm
      quoteType={desk.quoteType}
      onQuoteTypeChange={desk.setQuoteType}
      buyOpen={desk.buyOpen}
      sellOpen={desk.sellOpen}
      pairKey={desk.pairKey}
      onPairChange={desk.onPairChange}
      pairOptions={desk.pairOptions}
      spotPairs={desk.spotPairs}
      baseOptions={desk.baseOptions}
      amountAsset={desk.amountAsset}
      onAmountAssetChange={desk.setAmountCoin}
      amount={desk.amount}
      onAmountChange={desk.setAmount}
      onMax={desk.onMax}
      minBase={desk.minBase}
      amountApprox={desk.amountApprox}
      minQuoteLabel={desk.minQuoteLabel}
      amountError={desk.amountError}
      amountWarning={desk.amountWarning}
      available={desk.available}
      availableAsset={desk.availableAsset}
      lockedBalance={desk.lockedBalance}
      getBalance={desk.getBalance}
      assetsLoading={desk.assetsLoading}
      submitting={desk.submitting}
      deskOpen={desk.deskOpen}
      isLoggedIn={desk.isLoggedIn}
      loginHref={desk.loginHref}
      ticker={desk.ticker}
      onSubmit={onSubmit || desk.submitRfq}
      passThrough={passThrough}
    />
  );
}

const OtcDeskPage = () => {
  const desk = useOtcDesk();
  const location = useLocation();
  const navigate = useNavigate();
  const forceChart = /\/rfq\/?$/.test(location.pathname);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const otcDraft = {
    amount: desk.amount,
    amountAsset: desk.amountAsset,
    pairKey: desk.pairKey || "BTC/USDT",
    quoteType: desk.quoteType,
  };

  const requestFromLanding = () => {
    navigate("/otc/rfq", { state: { otcDraft } });
  };

  // Execution view with chart and ticket panel matching Figma screenshot
  if (forceChart) {
    return <OtcRfqTradingPage />;
  }

  // Full Landing Page matching Screenshot HI-FI
  return (
    <>
      <Helmet>
        <title>OTC Desk | AGCX</title>
        <meta
          name="description"
          content="OTC Desk Built for large crypto trades. Execute high-value crypto trades through a dedicated OTC desk with competitive pricing, deep liquidity, and personalized settlement support."
        />
      </Helmet>

      <div className="otc-desk-page">
        {/* =================================================================
            1. HERO SECTION WITH otcbnrbg.jpg & 3 FLOATING BADGES
            ================================================================= */}
        <section className="otc_desk_hero_s">
          <div className="otc-hero-badges-wrapper">
            {/* 3 Circular Badges with bright white glow positioned on the middle arc */}
            <div className="otc-hero-badge badge-top">
              <img className="dark_img" src="/images/otc-desk-img/hero_otc_vector2_light.png" alt="Candlesticks" />
              <img className="light_img" src="/images/otc-desk-img/hero_otc_vector2.png" alt="Candlesticks" />
            </div>
            <div className="otc-hero-badge badge-left">
              <img className="dark_img" src="/images/otc-desk-img/hero_otc_vector_light.png" alt="Sparkles" />
              <img className="light_img" src="/images/otc-desk-img/hero_otc_vector.png" alt="Sparkles" />
            </div>
            <div className="otc-hero-badge badge-right">
              <img className="dark_img" src="/images/otc-desk-img/hero_otc_vector3_light.png" alt="Lightning" />
              <img className="light_img" src="/images/otc-desk-img/hero_otc_vector3.png" alt="Lightning" />
            </div>
          </div>

          <div className="oct_bnr_img">
            <img src="/images/otc-desk-img/herobnr_img.png" alt="OTC Banner" />
          </div>

          <div className="otc-container">
            <div className="otc-hero-content">
              <h1 className="otc-hero-title">
                <span className="hero-gold-text">OTC Desk</span> Built<br />
                for large crypto trades.
              </h1>
              <p className="otc-hero-desc">
                Execute high-value crypto trades through a dedicated OTC desk with competitive pricing, deep liquidity, and personalized settlement support.
              </p>
              <div className="otc-hero-btns">
                <Link to="/otc/rfq" state={{ otcDraft }} className="btn-primary-gold">
                  Trade Now <i className="ri-arrow-right-line"></i>
                </Link>
                <Link to="/#support" className="btn-secondary-dark">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>

          <div className="oct_bottom_img">
            <img src="/images/otc-desk-img/herobottombg.png" alt="OTC Banner" />
          </div>

        </section>

        {/* =================================================================
            2. COMPLETE TRADING SUITE (REQUEST A QUOTE + HOW IT WORKS)
            ================================================================= */}
        <section className="otc-trading-suite-section">
          <div className="otc-container">
            <div className="otc-suite-header">
              <h2 className="otc-suite-title">
                Complete Trading Suite
              </h2>
            </div>

            <div className="otc-quote-works-grid" id="quote-section">
              {/* Left Card: Request a Quote Form */}
              <div className="otc-card otc-rfq-card">
                <RfqFields desk={desk} onSubmit={requestFromLanding} passThrough />
              </div>

              {/* Right Card: How It OTC Works? */}
              <div className="otc-card otc-works-card" id="how-it-works">
                <h3 className="otc-card-title">How It OTC Works?</h3>
                <div className="works-steps-list">
                  <div className="works-step-item">
                    <div className="works-step-num">1</div>
                    <div className="works-step-text">
                      <h4>Submit Quote</h4>
                      <p>Enter the amount you want to trade and submit your quote request.</p>
                    </div>
                  </div>
                  <div className="works-step-item">
                    <div className="works-step-num">2</div>
                    <div className="works-step-text">
                      <h4>Confirm Quote</h4>
                      <p>Our expert brokers confirm your quote at the best market rate.</p>
                    </div>
                  </div>
                  <div className="works-step-item">
                    <div className="works-step-num">3</div>
                    <div className="works-step-text">
                      <h4>Transfer Funds</h4>
                      <p>Send your funds to our secure escrow wallet.</p>
                    </div>
                  </div>
                  <div className="works-step-item">
                    <div className="works-step-num">4</div>
                    <div className="works-step-text">
                      <h4>Confirm Order</h4>
                      <p>Receive your assets directly once funds are verified.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =================================================================
            3. WHY TRADE WITH AGCE OTC DESK?
            ================================================================= */}
        <section className="why-otc-section">
          <div className="otc-container">
            <h2 className="why-otc-title">Why Trade with AGCE OTC Desk?</h2>
            <div className="why-otc-grid">
              {WHY_TRADE_LIST.map((item) => (
                <div className="why-otc-card" key={item.title}>
                  <div className="why-otc-icon-box">
                    <img src={item.icon} alt={item.title} />
                  </div>
                  <div className="why-otc-text">
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =================================================================
            4. BUILT FOR TRADES THAT NEED MORE THAN AN EXCHANGE SCREEN
            ================================================================= */}
        <section className="built-trades-section">
          <div className="otc-container">
            <div className="built-trades-top">
              <div className="built-trades-left">
                <h2 className="built-trades-heading">
                  Built for trades that need<br />
                  <span className="gold-text">more than an exchange<br />screen.</span>
                </h2>
                <p className="built-trades-desc">
                  Large orders can create unnecessary market impact when executed through public order books. Our OTC desk provides direct execution and tailored liquidity for high-value transactions.
                </p>
              </div>
              <div className="built-trades-right">
                <img
                  src="/images/otc-desk-img/built_rades_bg.jpg"
                  alt="Built for trades graphic"
                  className="built-trades-img light_img"
                />
                <img
                  src="/images/otc-desk-img/built_rades_bg_light.jpg"
                  alt="Built for trades graphic"
                  className="built-trades-img dark_img"
                />
              </div>
            </div>

            <div className="built-trades-features">
              {BUILT_FEATURES.map((item) => (
                <div className="built-feature-card" key={item.title}>
                  <div className="built-feature-icon-wrapper">
                    <img src={item.icon} alt={item.title} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default OtcDeskPage;
